/**
 * What is still running, top right, while you work somewhere else.
 *
 * Deliberately small and non-blocking: a pill per task, a spinner while it runs, its
 * outcome for a few seconds, then gone. It sits above the modals (they stop at 10000)
 * because the work it reports on is exactly the kind you start from a modal and then
 * leave — an Alpha transfer, a ServiceBox deletion.
 */
import { useBackgroundTasks, dismissBackgroundTask, type BackgroundTask } from "@services/backgroundTasks";

const Spinner = () => (
  <div className="w-3.5 h-3.5 shrink-0 rounded-full border-2 border-neutral-200 border-t-neutral-600 animate-spin" />
);

const Check = () => (
  <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 shrink-0" aria-hidden>
    <path d="M3.5 8.5l3 3 6-6.5" fill="none" stroke="#15803d" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Cross = () => (
  <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 shrink-0" aria-hidden>
    <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const STYLES: Record<BackgroundTask["status"], string> = {
  running: "bg-white/90 border-neutral-200 text-neutral-700",
  waiting: "bg-white/90 border-neutral-200 text-neutral-500",
  done: "bg-green-50/95 border-green-200 text-green-800",
  error: "bg-red-50/95 border-red-200 text-red-800",
};

export default function BackgroundTasksOverlay() {
  const tasks = useBackgroundTasks((s) => s.tasks);
  if (tasks.length === 0) return null;

  return (
    <div className="fixed top-3 right-3 z-[10050] flex flex-col items-end gap-2 pointer-events-none">
      {tasks.map((task) => (
        <button
          key={task.id}
          type="button"
          onClick={() => dismissBackgroundTask(task.id)}
          title="Masquer"
          className={`pointer-events-auto flex flex-row items-center gap-2.5 max-w-350 text-left
            backdrop-blur-sm border rounded-full pl-3 pr-3.5 py-1.5 shadow-md shadow-neutral-200
            transition-opacity ${STYLES[task.status]}`}
        >
          {task.status === "done" ? <Check /> : task.status === "error" ? <Cross /> : <Spinner />}
          <span className="flex flex-col leading-tight min-w-0">
            <span className="text-h5 font-medium truncate">{task.label}</span>
            {task.detail && <span className="text-h6 opacity-70 truncate">{task.detail}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}
